/* eslint-disable no-nested-ternary */
import { Common, color } from '@stockbit/ui';
import { LoadingOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import Image from 'global/Image';
import { AVATAR_URL } from 'constants/api';
import { handleErrorAvatar } from 'utils/general';
import { LabelVerifiedAccount } from 'global/Labels';
import { VerifiedStatus } from 'lib/api/verifiedBadge';
import {
  BoxContainer,
  BoxWrapper,
  CloseIcon,
  CloseIconWrapper,
} from './styled';
import {
  FOLLOW_ACTION,
  Follow,
  IDeleteSuggestedItem,
  IFollowMutation,
  QUERY_KEY_DATA,
} from './constants';
import { deleteSuggestedItem, followUser } from './helper';
import { Suggestions } from '../../../@types/friend';
import {
  DATA_CY_NAME_SUGGESTED_PEOPLE_STREAM_WIDGET_SUGGESTED_ITEM_INDEX,
  DATA_CY_NAME_SUGGESTED_PEOPLE_STREAM_WIDGET_SUGGESTED_ITEM_FOLLOW,
  DATA_CY_NAME_SUGGESTED_PEOPLE_STREAM_WIDGET_SUGGESTED_ITEM_REMOVE,
} from './__tests__/constant';

export interface IUserProp {
  user: {
    userid: number | string;
    username: string;
    user_avatar: string;
    is_verified: boolean;
    verified_status?: VerifiedStatus;
  };
  isFollowed: boolean;
  reason: string;
  index: number;
  [key: string]: any;
}

const { Flex, Text, Avatar, Button } = Common;

function SuggestedItem(props: IUserProp) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const {
    isFollowed,
    reason,
    user: { userid, username, user_avatar: avatar, is_verified: isVerified, ...restUserProps },
    index,
  } = props;

  const avatarSrc = `${AVATAR_URL}/${avatar}`;

  const followMutation = useMutation({
    mutationKey: [QUERY_KEY_DATA.suggested],
    mutationFn: followUser,
    onSuccess: (_, variables: IFollowMutation) =>
      queryClient.setQueryData(
        [QUERY_KEY_DATA.suggested],
        (oldData: Suggestions[]) => {
          const { userid: updatedUserId, type: updatedType } = variables;

          const newArr = oldData.map((data) => {
            if (Number(data.user.userid) === Number(updatedUserId)) {
              return {
                ...data,
                user: {
                  ...data.user,
                  follow: updatedType === FOLLOW_ACTION.FOLLOW,
                },
              };
            }

            return data;
          });

          setLoading(false);

          return newArr;
        },
      ),
  });

  const handleFollow = async () => {
    const followType: Follow = isFollowed
      ? FOLLOW_ACTION.UNFOLLOW
      : FOLLOW_ACTION.FOLLOW;
    const data = {
      userid,
      type: followType,
    };

    setLoading(true);

    followMutation.mutate(data);
  };

  const removeMutation = useMutation({
    mutationKey: [QUERY_KEY_DATA.suggested],
    mutationFn: deleteSuggestedItem,
    onSuccess: (_, variables: IDeleteSuggestedItem) =>
      queryClient.setQueryData(
        [QUERY_KEY_DATA.suggested],
        (oldData: Suggestions[]) => {
          const { id } = variables;

          return oldData.filter((__, indexData) => indexData !== id);
        },
      ),
  });

  const removeItem = () => {
    removeMutation.mutate({ id: index });
  };

  return (
    <BoxWrapper
      data-cy={DATA_CY_NAME_SUGGESTED_PEOPLE_STREAM_WIDGET_SUGGESTED_ITEM_INDEX}
    >
      <BoxContainer {...props}>
        <Flex direction="column" align="center">
          <Link href={`/${username}`} passHref>
            <Avatar size="large" padding="0 0 12px" as="a" target="_blank">
              <Image
                alt=""
                src={avatarSrc}
                onError={(e) => handleErrorAvatar(e, username)}
              />
            </Avatar>
          </Link>
          <Link href={`/${username}`} passHref>
            <Flex
              as="a"
              align="center"
              justify="center"
              mb="4px"
              target="_blank"
            >
              <Text
                family="bold"
                size="13px"
                ellipsis
                align="center"
                style={{
                  maxWidth: '100px',
                }}
              >
                {username}
              </Text>
              <LabelVerifiedAccount
                deprecatedVerifiedStatus={isVerified}
                verifiedStatus={restUserProps?.verified_status}
              />
            </Flex>
          </Link>
          <Text
            size="13px"
            height="16px"
            mb="16px"
            color={color.midgray}
            align="center"
          >
            {reason}
          </Text>
        </Flex>
        <Button
          data-cy={
            DATA_CY_NAME_SUGGESTED_PEOPLE_STREAM_WIDGET_SUGGESTED_ITEM_FOLLOW
          }
          disabled={loading}
          type={isFollowed ? 'default' : 'primary'}
          block
          onClick={handleFollow}
          radius="4px"
          height="28px"
        >
          <Text
            size="13px"
            color={isFollowed ? color.primary : color.light}
            align="center"
            family="bold"
          >
            {loading ? (
              <LoadingOutlined />
            ) : isFollowed ? (
              'Following'
            ) : (
              'Follow'
            )}
          </Text>
        </Button>
        <CloseIconWrapper>
          <CloseIcon
            onClick={removeItem}
            data-cy={
              DATA_CY_NAME_SUGGESTED_PEOPLE_STREAM_WIDGET_SUGGESTED_ITEM_REMOVE
            }
          />
        </CloseIconWrapper>
      </BoxContainer>
    </BoxWrapper>
  );
}

export default SuggestedItem;
